import { motion, useReducedMotion } from 'motion/react';
import type { ReactElement } from 'react';
import { useRef, useEffect, useMemo } from 'react';
import cx from 'clsx';
import { CheckmarkAnimatedTimelineIcon } from './icons/checkmark-animated-timeline';
import { CheckmarkTimelineIcon } from './icons/checkmark-timeline';
import styles from './timeline.module.css';

type TimelineStateId = string;

interface TimelineState {
  id: TimelineStateId;
  label: string;
}

function getStateId(state: TimelineStateId | TimelineState): TimelineStateId {
  return typeof state === 'string' ? state : state.id;
}

interface BaseTimelineSection {
  name: string;
  label: string;
}

export interface FlatTimelineSection extends BaseTimelineSection {
  states: TimelineStateId[];
}

export interface DetailedTimelineSection extends BaseTimelineSection {
  states: TimelineState[];
}

interface BaseTimelineProps {
  /** Displayed timeline granularity (section or state) */
  granularity?: 'section' | 'state';
  /** Current registration state */
  currentState: TimelineStateId;
}

export interface FlatTimelineProps extends BaseTimelineProps {
  granularity?: 'section';
  sections: FlatTimelineSection[];
}

interface DetailedTimelineProps extends BaseTimelineProps {
  granularity?: 'state';
  sections: DetailedTimelineSection[];
}

export type TimelineProps = FlatTimelineProps | DetailedTimelineProps;

// Animation easing curve (cubic-bezier)
const EASING = [0.4, 0, 0.2, 1] as const;

const COLORS = {
  dotInactive: 'var(--border-secondary)',
  dotActive: 'var(--background-primary)',
  labelInactive: 'var(--content-tertiary)',
  labelActive: 'var(--content-primary-a)',
} as const;

const SCALE = {
  pulseStart: 1,
  pulseMax: 4,
  pulseEnd: 1,
  dotBounceStart: 1,
  dotBouncePeak: 1.2,
  dotBounceEnd: 1,
} as const;

const ANIMATION_CONFIG = {
  activeDotScale: { duration: 0.2, ease: 'easeOut' },
  pulseRing: {
    duration: 2,
    ease: [0.4, 0, 0.6, 1],
    times: [0, 0.5, 0.8, 1] as number[],
    repeat: Infinity,
    delay: 1,
  },
  inactiveDot: { duration: 0.4, ease: EASING },
  lineProgress: { duration: 0.5, ease: EASING },
  staggerDelay: 0.1,
} as const;

const CHECKMARK_COMPLETION_TIME = 0.4;
const ACTIVE_ELEMENT_DELAY = CHECKMARK_COMPLETION_TIME * 2 + ANIMATION_CONFIG.lineProgress.duration;

const DOT_SIZE = {
  initial: 4,
  final: 8,
} as const;

const NO_ANIMATION = { duration: 0 } as const;

const activeDotVariants = {
  hidden: {
    scale: SCALE.dotBounceStart,
    backgroundColor: COLORS.dotInactive,
  },
  visible: {
    scale: [SCALE.dotBounceStart, SCALE.dotBouncePeak, SCALE.dotBounceEnd],
    backgroundColor: COLORS.dotActive,
  },
  immediate: {
    backgroundColor: COLORS.dotActive,
  },
};

const pulseRingVariants = {
  hidden: {
    opacity: 0,
    scale: SCALE.pulseStart,
  },
  visible: {
    scale: [SCALE.pulseStart, SCALE.pulseMax, SCALE.pulseEnd],
    opacity: [1, 0, 0, 0],
  },
};

interface DotRendererProps {
  isActive: boolean;
  isDone: boolean;
  index: number;
  isFirstRender: boolean;
  prefersReducedMotion: boolean;
  isMovingForward: boolean;
}

function getActiveDotTransition(shouldAnimateTransition: boolean) {
  if (!shouldAnimateTransition) return NO_ANIMATION;
  return { ...ANIMATION_CONFIG.activeDotScale, repeat: 0, delay: ACTIVE_ELEMENT_DELAY };
}

function getLabelTransition(
  isActive: boolean,
  isMovingForward: boolean,
  prefersReducedMotion: boolean
) {
  if (prefersReducedMotion) return NO_ANIMATION;
  if (!isActive || !isMovingForward) return NO_ANIMATION;

  return { duration: ANIMATION_CONFIG.activeDotScale.duration, delay: ACTIVE_ELEMENT_DELAY };
}

// Render active dot with pulse animation
function ActiveDot({
  shouldAnimate,
  shouldAnimateTransition,
}: {
  shouldAnimate: boolean;
  shouldAnimateTransition: boolean;
}): ReactElement {
  const animateVariant = shouldAnimateTransition ? 'visible' : 'immediate';
  const initialVariant = shouldAnimateTransition ? 'hidden' : 'immediate';

  return (
    <>
      {shouldAnimate ? (
        <motion.div
          animate="visible"
          className={styles.pulseRing}
          initial="hidden"
          transition={ANIMATION_CONFIG.pulseRing}
          variants={pulseRingVariants}
        />
      ) : null}
      <motion.div
        animate={animateVariant}
        className={styles.activeDot}
        initial={initialVariant}
        transition={getActiveDotTransition(shouldAnimateTransition)}
        variants={activeDotVariants}
      />
    </>
  );
}

function DoneCheckmark({ prefersReducedMotion }: { prefersReducedMotion: boolean }): ReactElement {
  return prefersReducedMotion ? (
    <CheckmarkTimelineIcon className={styles.checkIcon} />
  ) : (
    <CheckmarkAnimatedTimelineIcon className={styles.checkIcon} />
  );
}

function InactiveDot({
  shouldAnimate,
  index,
}: {
  shouldAnimate: boolean;
  index: number;
}): ReactElement {
  const animateProps = shouldAnimate
    ? { width: DOT_SIZE.final, height: DOT_SIZE.final, opacity: 1, scale: 1, rotate: 0 }
    : {};

  const initialProps = shouldAnimate
    ? {
        width: DOT_SIZE.initial,
        height: DOT_SIZE.initial,
        opacity: 0,
        scale: 0.5,
      }
    : {};

  const transition = shouldAnimate
    ? { ...ANIMATION_CONFIG.inactiveDot, delay: index * ANIMATION_CONFIG.staggerDelay }
    : NO_ANIMATION;

  return (
    <motion.div
      animate={animateProps}
      className={styles.dot}
      initial={initialProps}
      transition={transition}
    />
  );
}

function DotRenderer({
  isActive,
  isDone,
  index,
  isFirstRender,
  prefersReducedMotion,
  isMovingForward,
}: DotRendererProps): ReactElement | null {
  const shouldAnimate = !prefersReducedMotion;
  const shouldAnimateTransition = shouldAnimate && isMovingForward;

  if (isActive) {
    return (
      <ActiveDot shouldAnimate={shouldAnimate} shouldAnimateTransition={shouldAnimateTransition} />
    );
  }

  if (isDone) {
    return <DoneCheckmark prefersReducedMotion={prefersReducedMotion || isFirstRender} />;
  }

  return <InactiveDot index={index} shouldAnimate={shouldAnimate} />;
}

export function Timeline({
  sections,
  currentState,
  granularity = 'section',
}: TimelineProps): JSX.Element {
  const prefersReducedMotion = useReducedMotion() ?? false;

  const currentSectionIndex = sections.findIndex(s =>
    s.states.some(state => getStateId(state) === currentState)
  );

  const currentStateIndex = sections[currentSectionIndex]?.states.findIndex(
    state => getStateId(state) === currentState
  );

  const previousIndexRef = useRef<number | null>(null);
  const isFirstRender = previousIndexRef.current === null;
  const isMovingForward = isFirstRender || currentSectionIndex > (previousIndexRef.current ?? -1);

  useEffect(() => {
    previousIndexRef.current = currentSectionIndex;
  }, [currentSectionIndex]);

  return (
    <ul className={styles.timeline}>
      {sections.map((section, index) => {
        const isLastSection = index === sections.length - 1;
        const isDoneSection = Boolean(currentSectionIndex > index);
        const isActiveSection = Boolean(
          section.states.some(state => getStateId(state) === currentState) && !isDoneSection
        );

        const showLine = useMemo(() => {
          if (granularity === 'section') {
            return !isLastSection;
          }
          return isLastSection ? isActiveSection : true;
        }, [isLastSection, isActiveSection]);

        let lineHeight = '0';
        if (isDoneSection) {
          lineHeight = '100%';
        } else if (isActiveSection && granularity === 'state') {
          const TOP_OFFSET = 8;
          const STEP_HEIGHT = 16;
          const STEP_GAP = 8;

          lineHeight = `${TOP_OFFSET + STEP_HEIGHT / 2 + (currentStateIndex ?? 0) * (STEP_HEIGHT + STEP_GAP)}px`;
        }

        return (
          <li className={styles.section} key={section.name}>
            <div className={styles.sectionTop}>
              <div className={styles.dotWrapper}>
                <DotRenderer
                  index={index}
                  isActive={isActiveSection}
                  isDone={isDoneSection}
                  isFirstRender={isFirstRender}
                  isMovingForward={isMovingForward}
                  prefersReducedMotion={prefersReducedMotion}
                />
              </div>

              <motion.span
                animate={{
                  color:
                    isActiveSection || isDoneSection ? COLORS.labelActive : COLORS.labelInactive,
                }}
                className="body-standard"
                data-test-timeline-section={section.name}
                initial={false}
                transition={getLabelTransition(
                  isActiveSection,
                  isMovingForward,
                  prefersReducedMotion
                )}
              >
                {section.label}
              </motion.span>
            </div>

            <div className={styles.sectionContent}>
              {granularity === 'state' && isActiveSection ? (
                <ul className={styles.stepsList}>
                  {section.states.map((state, stateIndex) => {
                    const id = getStateId(state);
                    const label = typeof state === 'string' ? state : state.label;
                    const isHighlightedStep = stateIndex <= (currentStateIndex ?? 0);
                    return (
                      <li className={cx(styles.step, isHighlightedStep && styles.active)} key={id}>
                        {label}
                      </li>
                    );
                  })}
                </ul>
              ) : null}

              {showLine ? (
                <div className={styles.lineWrapper}>
                  <div className={styles.line}>
                    <motion.div
                      animate={{ height: lineHeight }}
                      className={styles.lineProgress}
                      initial={{ height: 0 }}
                      transition={
                        prefersReducedMotion || isFirstRender
                          ? NO_ANIMATION
                          : {
                              ...ANIMATION_CONFIG.lineProgress,
                              // Line animates after checkmark completes + stagger delay
                              delay: isDoneSection
                                ? CHECKMARK_COMPLETION_TIME + index * ANIMATION_CONFIG.staggerDelay
                                : 0,
                            }
                      }
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
